import Alert from '@mui/material/Alert';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import LinearProgress from '@mui/material/LinearProgress';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import dayjs from 'dayjs';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { logError } from '../../../utils/logger';
import { ApprovedQuote, Billable, BillableMilestone, BillDetail, billingApi, BillType, rupees } from '../billing.api';

const GST_HALF = 0.09;
const round2 = (n: number) => Math.round(n * 100) / 100;

/**
 * Raise a proforma or a bill from an approved quotation: pick the quotation, tick the
 * milestones to bill (each priced from the quotation, read-only), fill in the references, and
 * see the total with CGST + SGST before raising it. Milestones already on a bill are shown
 * but cannot be ticked for another bill, and milestones are billed in order: one can be ticked
 * only once every milestone before it is ticked or already on a bill or open proforma.
 */
export default function NewBillDialog({
  open,
  billType,
  onClose,
  onCreated,
}: {
  open: boolean;
  billType: BillType;
  onClose: () => void;
  onCreated: (bill: BillDetail) => void;
}) {
  const [quotes, setQuotes] = useState<ApprovedQuote[]>([]);
  const [quote, setQuote] = useState<ApprovedQuote | null>(null);
  const [billable, setBillable] = useState<Billable | null>(null);
  const [picked, setPicked] = useState<Record<number, string>>({});
  const [billDate, setBillDate] = useState(dayjs().format('YYYY-MM-DD'));
  const [referenceText, setReferenceText] = useState('');
  const [paymentRequestNo, setPaymentRequestNo] = useState('');
  const [paymentRequestDate, setPaymentRequestDate] = useState(dayjs().format('YYYY-MM-DD'));
  const [workOrderNo, setWorkOrderNo] = useState('');
  const [workOrderDate, setWorkOrderDate] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isBill = billType === 'INVOICE';

  // Fresh each time it opens.
  useEffect(() => {
    if (!open) return;
    setQuote(null);
    setBillable(null);
    setPicked({});
    setBillDate(dayjs().format('YYYY-MM-DD'));
    setReferenceText('');
    setPaymentRequestNo('');
    setPaymentRequestDate(dayjs().format('YYYY-MM-DD'));
    setWorkOrderNo('');
    setWorkOrderDate('');
    setError(null);
    billingApi
      .approvedQuotes()
      .then(setQuotes)
      .catch((e) => setError(logError({ message: 'Could not load the approved quotations', error: e, code: 'BILL_QUOTES_FAILED' })));
  }, [open]);

  useEffect(() => {
    if (!quote) return;
    let live = true;
    setLoading(true);
    setPicked({});
    billingApi
      .billable(quote.quoteId)
      .then((b) => {
        if (!live) return;
        setBillable(b);
        setReferenceText(b.referenceText ?? '');
      })
      .catch((e) => live && setError(logError({ message: 'Could not load the milestones', error: e, code: 'BILLABLE_FAILED' })))
      .finally(() => live && setLoading(false));
    return () => {
      live = false;
    };
  }, [quote]);

  const lines = useMemo(
    () =>
      (billable?.milestones ?? [])
        .filter((m) => picked[m.milestoneNumber] !== undefined)
        .map((m) => {
          const amount = round2(Number(picked[m.milestoneNumber] || 0));
          // V36 — a client in another state pays IGST (18%) in place of CGST + SGST (9% + 9%).
          const half = billable?.interState ? 0 : round2(amount * GST_HALF);
          const igst = billable?.interState ? round2(amount * GST_HALF * 2) : 0;
          return { milestone: m, amount, cgst: half, sgst: half, igst, total: round2(amount + half + half + igst) };
        }),
    [billable, picked]
  );
  const totals = lines.reduce(
    (t, l) => ({ amount: t.amount + l.amount, gst: t.gst + l.cgst + l.sgst + l.igst, total: t.total + l.total }),
    { amount: 0, gst: 0, total: 0 }
  );

  // Already on a tax invoice or an open proforma: counts as billed for the order of milestones.
  const settled = (m: BillableMilestone) => Boolean(m.invoicedIn || m.proformaIn);
  /** Ticking is allowed only once every earlier milestone is ticked or settled, so 1 → 3 cannot skip 2. */
  const canTick = (index: number) =>
    (billable?.milestones ?? []).slice(0, index).every((m) => settled(m) || picked[m.milestoneNumber] !== undefined);

  const toggle = (index: number) =>
    setPicked((p) => {
      const ms = billable?.milestones ?? [];
      const m = ms[index];
      const next = { ...p };
      if (next[m.milestoneNumber] !== undefined) {
        // Unticking one unticks every later one too, so no gap is left behind.
        ms.slice(index).forEach((later) => delete next[later.milestoneNumber]);
      } else {
        next[m.milestoneNumber] = String(m.amount);
      }
      return next;
    });

  const submit = async () => {
    if (!quote) return setError('Choose an approved quotation.');
    if (!lines.length) return setError('Tick at least one milestone to bill.');
    if (lines.some((l) => !(l.amount > 0))) return setError('Every ticked milestone needs an amount above zero.');
    setSaving(true);
    setError(null);
    try {
      const bill = await billingApi.create({
        billType,
        quoteId: quote.quoteId,
        billDate,
        items: lines.map((l) => ({ milestoneNumber: l.milestone.milestoneNumber, amount: l.amount })),
        referenceText: referenceText || undefined,
        paymentRequestNo: (!isBill && paymentRequestNo) || undefined,
        paymentRequestDate: (!isBill && paymentRequestDate) || undefined,
        workOrderNo: workOrderNo || undefined,
        workOrderDate: workOrderDate || undefined,
      });
      toast.success(`${isBill ? 'Bill' : 'Proforma'} ${bill.bill.billNo} raised`);
      onCreated(bill);
    } catch (e) {
      setError(logError({ message: `Could not raise the ${isBill ? 'bill' : 'proforma'}`, error: e, code: 'BILL_CREATE_FAILED' }));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth='md'>
      <DialogTitle>{isBill ? 'New Tax Invoice' : 'New Proforma Invoice'}</DialogTitle>
      <DialogContent>
        <Stack gap={2.5} sx={{ pt: 1 }}>
          {error && <Alert severity='error'>{error}</Alert>}
          <Autocomplete
            options={quotes}
            value={quote}
            onChange={(_, q) => setQuote(q)}
            getOptionLabel={(q) => `${q.jobCode ?? '—'} · ${q.jobName ?? ''} · ${q.clientName ?? 'No client'}`}
            isOptionEqualToValue={(a, b) => a.quoteId === b.quoteId}
            renderOption={(props, q) => (
              <Box component='li' {...props} key={q.quoteId}>
                <Stack>
                  <Typography variant='body2' fontWeight={600}>
                    {q.jobCode} · {q.jobName}
                  </Typography>
                  <Typography variant='caption' color='text.secondary'>
                    {q.clientName ?? 'No client'} · {q.companyName ?? 'No company set'} · approved{' '}
                    {dayjs(q.approvedAt).format('DD MMM YYYY')} · {rupees(q.quoteValue)} + GST
                  </Typography>
                </Stack>
              </Box>
            )}
            renderInput={(params) => (
              <TextField {...params} label='Approved quotation' placeholder='Search job, client or company' size='small' />
            )}
            noOptionsText='No approved quotation. Approve one on the job’s quotation list first.'
          />
          {quote && !quote.companyId && (
            <Alert severity='warning'>This job has no company set, so it cannot be billed yet. Set it on the job first.</Alert>
          )}
          {loading && <LinearProgress />}

          {billable && (
            // Scrolls inside the popup on a narrow screen rather than pushing it wider.
            <Box sx={{ overflowX: 'auto' }}>
            <Table size='small' sx={{ '& td, & th': { px: 1 } }}>
              <TableHead>
                <TableRow>
                  <TableCell padding='checkbox' />
                  <TableCell>Milestone</TableCell>
                  <TableCell align='right'>%</TableCell>
                  <TableCell align='right' sx={{ width: 140 }}>Amount</TableCell>
                  <TableCell align='right' sx={{ whiteSpace: 'nowrap' }}>{billable.interState ? 'IGST 18%' : 'GST 18%'}</TableCell>
                  <TableCell align='right' sx={{ whiteSpace: 'nowrap' }}>With GST</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {billable.milestones.map((m, index) => {
                  const on = picked[m.milestoneNumber] !== undefined;
                  const blocked = (isBill && Boolean(m.invoicedIn)) || (!on && !canTick(index));
                  const line = lines.find((l) => l.milestone.milestoneNumber === m.milestoneNumber);
                  return (
                    <TableRow key={m.milestoneNumber} hover>
                      <TableCell padding='checkbox'>
                        <Checkbox checked={on} disabled={blocked} onChange={() => toggle(index)} />
                      </TableCell>
                      <TableCell>
                        <Typography variant='body2'>
                          {m.milestoneNumber}. {m.description || `Milestone ${m.milestoneNumber}`}
                        </Typography>
                        <Stack direction='row' gap={0.5} sx={{ mt: 0.5 }}>
                          {m.invoicedIn && <Chip size='small' color='success' variant='outlined' label={`Billed: ${m.invoicedIn}`} />}
                          {m.proformaIn && <Chip size='small' variant='outlined' label={`Proforma: ${m.proformaIn}`} />}
                        </Stack>
                      </TableCell>
                      <TableCell align='right'>{m.paymentPercent ?? '—'}</TableCell>
                      <TableCell align='right'>
                        <Box component='span' sx={{ whiteSpace: 'nowrap' }}>{rupees(m.amount)}</Box>
                      </TableCell>
                      <TableCell align='right' sx={{ whiteSpace: 'nowrap' }}>{line ? rupees(line.cgst + line.sgst + line.igst) : rupees(Number(m.cgstAmount) + Number(m.sgstAmount) + Number(m.igstAmount ?? 0))}</TableCell>
                      <TableCell align='right' sx={{ whiteSpace: 'nowrap' }}>{line ? rupees(line.total) : rupees(m.totalAmount)}</TableCell>
                    </TableRow>
                  );
                })}
                <TableRow>
                  <TableCell />
                  <TableCell {...({ colSpan: 2 } as object)}>
                    <b>Total of ticked milestones</b>
                  </TableCell>
                  <TableCell align='right' sx={{ whiteSpace: 'nowrap' }}>
                    <b>{rupees(totals.amount)}</b>
                  </TableCell>
                  <TableCell align='right' sx={{ whiteSpace: 'nowrap' }}>
                    <b>{rupees(totals.gst)}</b>
                  </TableCell>
                  <TableCell align='right' sx={{ whiteSpace: 'nowrap' }}>
                    <b>{rupees(totals.total)}</b>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
            </Box>
          )}

          {billable && (
            /*
             * Two columns: a number field that takes the room, and a date beside it at a fixed
             * width so it is never squeezed. Four across used to crush the dates out of the popup.
             * One column on a narrow screen.
             */
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 180px' }, gap: 2, alignItems: 'start' }}>
              <TextField label='Reference to' size='small' fullWidth value={referenceText} onChange={(e) => setReferenceText(e.target.value)}
                helperText='Printed under "Reference to," e.g. Proposed Residential Building at Kothrud' />
              <TextField label='Bill date' type='date' size='small' fullWidth value={billDate} onChange={(e) => setBillDate(e.target.value)}
                InputLabelProps={{ shrink: true }} inputProps={{ max: dayjs().format('YYYY-MM-DD') }} />
              {/* The tax invoice no longer prints a payment request, so it is asked only on a proforma. */}
              {!isBill && (
                <>
                  <TextField label='Proforma invoice no.' size='small' fullWidth value={paymentRequestNo}
                    onChange={(e) => setPaymentRequestNo(e.target.value)} />
                  <TextField label='Proforma invoice date' type='date' size='small' fullWidth value={paymentRequestDate}
                    onChange={(e) => setPaymentRequestDate(e.target.value)} InputLabelProps={{ shrink: true }} />
                </>
              )}
              <TextField label='Work order no.' size='small' fullWidth value={workOrderNo} onChange={(e) => setWorkOrderNo(e.target.value)} />
              <TextField label='Work order date' type='date' size='small' fullWidth value={workOrderDate}
                onChange={(e) => setWorkOrderDate(e.target.value)} InputLabelProps={{ shrink: true }} />
            </Box>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button variant='contained' onClick={submit} disabled={saving || !lines.length || !quote?.companyId}>
          {saving ? 'Raising…' : `Raise ${isBill ? 'tax invoice' : 'proforma invoice'} · ${rupees(totals.total)}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
